// Часовой пояс по умолчанию, пока не загрузился текущий пользователь. Значение
// подменяется на пользовательский (users.timezone из настроек) плагином
// plugins/timezone.client.ts — все функции ниже читают эту переменную "по месту",
// поэтому существующие вызовы (без явного timezone) менять не нужно.
let TZ = 'Europe/Moscow'

export function setDisplayTimezone(tz: string) {
  if (!tz) return
  try {
    new Intl.DateTimeFormat('ru-RU', { timeZone: tz })
    TZ = tz
  } catch { /* неизвестный пояс — остаёмся на прежнем */ }
}

export function getDisplayTimezone(): string {
  return TZ
}

// «ГГГГ-ММ-ДД» без времени — это календарная дата, а не момент: форматируем как есть (UTC),
// иначе в поясах западнее UTC она показывалась бы предыдущим днём.
function tzFor(value: unknown): string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? 'UTC' : TZ
}

// ── Время в поясе пользователя ────────────────────────────────────────────────
// Сроки задач, события календаря и напоминания хранятся как настоящий UTC-момент.
// Поля ввода (дата + время) — это «настенное» время в поясе пользователя (TZ).
// Эти функции — единственное место перевода между ними.

const partsFmtCache = new Map<string, Intl.DateTimeFormat>()
function partsFmt(tz: string): Intl.DateTimeFormat {
  let f = partsFmtCache.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short',
    })
    partsFmtCache.set(tz, f)
  }
  return f
}

const WEEKDAY_IDX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

export interface ZonedParts {
  year: number; month: number; day: number; hours: number; minutes: number; seconds: number
  /** 0 = вс … 6 = сб (как Date#getDay) */
  weekday: number
  /** ГГГГ-ММ-ДД */
  date: string
  /** ЧЧ:ММ */
  time: string
}

/** Момент → дата/время в поясе пользователя. */
export function zonedParts(value: string | number | Date, tz: string = TZ): ZonedParts {
  const d = value instanceof Date ? value : new Date(value)
  const get: Record<string, string> = {}
  for (const p of partsFmt(tz).formatToParts(d)) get[p.type] = p.value
  const year = +get.year, month = +get.month, day = +get.day
  const hours = +get.hour % 24, minutes = +get.minute, seconds = +get.second
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    year, month, day, hours, minutes, seconds, weekday: WEEKDAY_IDX[get.weekday] ?? 0,
    date: `${year}-${pad(month)}-${pad(day)}`, time: `${pad(hours)}:${pad(minutes)}`,
  }
}

/** Смещение пояса от UTC в этот момент, мс (Москва: +3 ч). */
export function zoneOffsetMs(value: string | number | Date, tz: string = TZ): number {
  const d = value instanceof Date ? value : new Date(value)
  const p = zonedParts(d, tz)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hours, p.minutes, p.seconds)
  return asUtc - (Math.floor(d.getTime() / 1000) * 1000)
}

/** Дата ГГГГ-ММ-ДД + время ЧЧ:ММ в поясе пользователя → ISO-строка UTC. */
export function zonedToIso(date: string, time: string = '00:00', tz: string = TZ): string {
  const [y, mo, d] = date.split('-').map(Number)
  const [h, mi] = (time || '00:00').split(':').map(Number)
  const naive = Date.UTC(y, (mo || 1) - 1, d || 1, h || 0, mi || 0)
  // две итерации — корректно и на переходах летнего времени
  let ts = naive - zoneOffsetMs(naive, tz)
  ts = naive - zoneOffsetMs(ts, tz)
  return new Date(ts).toISOString()
}

/** ГГГГ-ММ-ДД момента в поясе пользователя (без значения — сегодня). */
export function zonedDate(value?: string | number | Date | null, tz: string = TZ): string {
  return zonedParts(value ?? Date.now(), tz).date
}

/** ЧЧ:ММ момента в поясе пользователя. */
export function zonedTime(value: string | number | Date, tz: string = TZ): string {
  return zonedParts(value, tz).time
}

/** Сегодня ГГГГ-ММ-ДД в поясе пользователя. */
export function todayYmd(tz: string = TZ): string {
  return zonedParts(Date.now(), tz).date
}

/** Момент → «настенное» время пользователя, записанное как UTC (для арифметики дат через getUTC*). */
export function toWallClock(value: string | number | Date, tz: string = TZ): Date {
  const d = value instanceof Date ? value : new Date(value)
  return new Date(d.getTime() + zoneOffsetMs(d, tz))
}

/** Обратно к toWallClock: «настенное» время (getUTC*) → настоящий момент UTC. */
export function fromWallClock(wall: Date): Date {
  return new Date(zonedToIso(wall.toISOString().slice(0, 10), wall.toISOString().slice(11, 16)))
}

/** "15 марта 2026" */
export function formatDate(value?: string | Date | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: tzFor(value),
  })
}

/** "15 мар., 18:00" */
export function formatDateTime(value?: string | Date | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  })
}

/** "15.03.2026" */
export function formatShortDate(value?: string | Date | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: tzFor(value),
  })
}

/** "15 мар., 18:00" (with midnight check — no time if 00:00) */
export function formatDateShort(value?: string | Date | null): string {
  if (!value) return '—'
  const date = new Date(value)
  const dateStr = date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    timeZone: TZ,
  })
  const hours = parseInt(date.toLocaleString('ru-RU', { hour: '2-digit', hour12: false, timeZone: TZ }))
  const minutes = parseInt(date.toLocaleString('ru-RU', { minute: '2-digit', timeZone: TZ }))
  if (hours === 0 && minutes === 0) {
    return dateStr
  }
  const timeStr = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  })
  return `${dateStr}, ${timeStr}`
}

/** "15 марта, 18:00" (same as formatDateShort but with the full month name) */
export function formatDateLongMonth(value?: string | Date | null): string {
  if (!value) return '—'
  const date = new Date(value)
  const dateStr = date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: TZ,
  })
  const hours = parseInt(date.toLocaleString('ru-RU', { hour: '2-digit', hour12: false, timeZone: TZ }))
  const minutes = parseInt(date.toLocaleString('ru-RU', { minute: '2-digit', timeZone: TZ }))
  if (hours === 0 && minutes === 0) {
    return dateStr
  }
  const timeStr = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  })
  return `${dateStr}, ${timeStr}`
}

/** "23 сентября в 09:00" (full month, "в" instead of a comma; no time if midnight).
 *  Год дописывается в конце, только если он не текущий: "23 сентября в 09:00, 2025". */
export function formatDateLongMonthAt(value?: string | Date | null): string {
  if (!value) return '—'
  const date = new Date(value)
  const dateStr = date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: TZ,
  })
  const yearOf = (d: Date) => d.toLocaleDateString('ru-RU', { year: 'numeric', timeZone: TZ })
  const year = yearOf(date)
  const otherYear = year !== yearOf(new Date())
  const hours = parseInt(date.toLocaleString('ru-RU', { hour: '2-digit', hour12: false, timeZone: TZ }))
  const minutes = parseInt(date.toLocaleString('ru-RU', { minute: '2-digit', timeZone: TZ }))
  if (hours === 0 && minutes === 0) {
    return otherYear ? `${dateStr} ${year}` : dateStr
  }
  const timeStr = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  })
  return otherYear ? `${dateStr} в ${timeStr}, ${year}` : `${dateStr} в ${timeStr}`
}

/** "18:00" */
export function formatTime(value?: string | Date | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  })
}

/** "понедельник, 15 марта 2026, 18:00" */
export function formatDateFull(value?: string | Date | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  })
}

/** "сегодня", "вчера", "15 марта 2026" */
export function formatRelativeDate(value?: string | Date | null): string {
  if (!value) return '—'
  const d = new Date(value)
  const now = new Date()
  // Compare dates in Moscow timezone
  const fmt = (dt: Date) => dt.toLocaleDateString('ru-RU', { timeZone: TZ })
  const todayStr = fmt(now)
  const yesterdayDate = new Date(now.getTime() - 86400000)
  const yesterdayStr = fmt(yesterdayDate)
  const dateStr = fmt(d)

  if (dateStr === todayStr) return 'Сегодня'
  if (dateStr === yesterdayStr) return 'Вчера'
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: TZ })
}

/** "15.03.2026" (date only, no time) */
export function formatDateOnly(value?: string | Date | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: tzFor(value),
  })
}

/** "18:00" (today) or "15.03" (other day) — for mail list */
export function formatDateOrTime(value?: string | Date | null): string {
  if (!value) return '—'
  const d = new Date(value)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'сейчас'
  if (mins < 60) return `${mins} мин`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: TZ })
  if (hours < 48) return 'вчера'
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: TZ })
}

/** "только что", "5 мин назад", "3 ч назад", "2 дн назад", "15 мар." */
export function formatRelativeShort(value?: string | Date | null): string {
  if (!value) return '—'
  const date = new Date(value)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'только что'
  if (diffMin < 60) return `${diffMin} мин назад`
  if (diffHours < 24) return `${diffHours} ч назад`
  if (diffDays < 7) return `${diffDays} дн назад`
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', timeZone: TZ })
}

/** "15.03 18:00" — short date + time */
export function formatDateTimeShort(value?: string | Date | null): string {
  if (!value) return ''
  const d = new Date(value)
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: TZ }) + ' ' +
    d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: TZ })
}

/** "15.03.2026 18:00" — full date + time */
export function formatDateTimeFull(value?: string | Date | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  })
}

/** "15.03" — day.month only */
export function formatDayMonth(value?: string | Date | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    timeZone: tzFor(value),
  })
}

/** "март 2026" — month label for calendars */
export function formatMonthYear(value: Date): string {
  return value.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric', timeZone: TZ })
}

/** "15 мар." — day + short month, no year */
export function formatDayMonthShort(value?: string | Date | null): string {
  if (!value) return ''
  return new Date(value).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', timeZone: TZ })
}
